<template>
  <b-modal id="usageDetailsModal" size="xl" :title="$t('licenseUsageDetails')" hide-footer @show="onShow" @hidden="onHidden">
    <div v-if="license">
      <b-row class="mb-3">
        <b-col md="6">
          <dl class="row mb-0">
            <dt class="col-sm-5">{{ $t('organizationNumber') }}</dt>
            <dd class="col-sm-7">{{ license.ownerOrganizationNumber }}</dd>
            <dt class="col-sm-5">{{ $t('schoolName') }}</dt>
            <dd class="col-sm-7">{{ license.organizationName || license.ownerOrganizationNumber }}</dd>
            <dt class="col-sm-5">{{ $t('ean_isbn') }}</dt>
            <dd class="col-sm-7">{{ license.ean }}</dd>
            <dt class="col-sm-5">{{ $t('name') }}</dt>
            <dd class="col-sm-7">
              {{ license.productName }}
              <small v-if="productSubtitle" class="text-muted d-block">{{ productSubtitle }}</small>
            </dd>
          </dl>
        </b-col>
        <b-col md="6">
          <dl class="row mb-0">
            <dt class="col-sm-5">{{ $t('purchasedLicenses') }}</dt>
            <dd class="col-sm-7">{{ license.usageRights }}</dd>
            <dt class="col-sm-5">{{ $t('actualUsage') }}</dt>
            <dd class="col-sm-7">
              <span v-if="actualUsage != null">{{ actualUsage }}</span>
              <b-spinner v-else small/>
              <b-badge v-if="actualUsage > license.usageRights" variant="danger" class="ml-1">{{ $t('overuse') }}</b-badge>
            </dd>
            <dt class="col-sm-5">{{ $t('totalUsage') }}</dt>
            <dd class="col-sm-7">
              <span v-if="resultCount != null">{{ resultCount }}</span>
              <b-spinner v-else small/>
              <b-badge v-if="resultCount > license.usageRights" variant="warning" class="ml-1">{{ $t('excessUsage') }}</b-badge>
            </dd>
          </dl>
        </b-col>
      </b-row>

      <b-row class="mb-2">
        <b-col>
          <b-button size="sm" variant="primary" :disabled="showAssign" @click="showAssign = true">
            {{ $t('addUsers') }}
          </b-button>
        </b-col>
      </b-row>

      <assign-users v-if="showAssign" :license="license" :existing-user-ids="existingUserIds"
                    @assigned="refresh" @close="showAssign = false"/>

      <b-alert variant="danger" dismissible :show="forceError" @dismissed="forceError = false">
        {{ $t('forceError') }}
      </b-alert>

      <b-table striped hover small class="aco-table"
               sticky-header="60vh"
               :busy="isBusy && updatingUserId == null"
               :items="response"
               :fields="fields">
        <template #table-busy>
          <div class="text-center">
            <b-spinner class="align-middle"></b-spinner>
          </div>
        </template>
        <template slot="empty">
          <div class="text-center">{{ $t('noResults') }}</div>
        </template>
        <template v-slot:cell(fullName)="row">
          <span v-if="!row.item.username"><i>{{ $t('deleted') }}</i></span>
          <span v-else>{{ row.item.fullName }}</span>
        </template>
        <template v-slot:cell(status)="row">
          <b-badge :variant="statusVariant(row.item.status)">
            {{ translateAlternatives('licenseUsageStatuses', row.item.status) }}
          </b-badge>
        </template>
        <template v-slot:cell(override)="row">
          <div class="d-flex align-items-center text-nowrap">
            <b-button-group size="sm">
              <b-button v-for="option in overrideOptions" :key="option.value"
                        :variant="forcedValue(row.item) === option.value ? option.variant : 'outline-secondary'"
                        :pressed="forcedValue(row.item) === option.value"
                        :disabled="updatingUserId != null"
                        @click="force(row.item, option.value)">
                {{ option.text }}
              </b-button>
            </b-button-group>
            <b-spinner v-if="updatingUserId === row.item.userId" small class="ml-2"/>
          </div>
        </template>
      </b-table>
    </div>
  </b-modal>
</template>

<script>
  import BaseComponent from '../../components/BaseComponent'
  import { mapGetters } from 'vuex'
  import LicenseService from '../../services/LicenseService'
  import AssignUsers from './AssignUsers'

  export default {
    name: 'UsageDetails',
    extends: BaseComponent,
    components: { AssignUsers },
    props: ['license', 'actualUsage'],

    data () {
      return {
        productSubtitle: null,
        updatingUserId: null,
        forceError: false,
        showAssign: false
      }
    },

    computed: {
      ...mapGetters('licenseUsages', ['response', 'resultCount', 'isBusy']),
      existingUserIds () {
        return this.response.map(usage => usage.userId)
      },
      fields () {
        return [
          { key: 'fullName', label: this.$t('name'), sortable: true },
          { key: 'username', label: this.$t('username'), sortable: true },
          { key: 'role', label: this.$t('role'), sortable: true, formatter: value => this.translateAlternatives('userPrimaryRoles', value) },
          { key: 'firstAccess', label: this.$t('firstAccess'), sortable: true, tdClass: 'text-nowrap', formatter: 'formatDate' },
          { key: 'latestAccess', label: this.$t('latestAccess'), sortable: true, tdClass: 'text-nowrap', formatter: 'formatDate' },
          { key: 'accessCount', label: this.$t('accessCount'), sortable: true },
          { key: 'status', label: this.$t('status') },
          { key: 'override', label: this.$t('override') }
        ]
      },
      overrideOptions () {
        return [
          { value: 'NO', text: this.$t('automatic'), variant: 'secondary' },
          { value: 'ACTIVE', text: this.translateAlternatives('licenseUsageStatuses', 'ACTIVE'), variant: 'primary' },
          { value: 'PASSIVE', text: this.translateAlternatives('licenseUsageStatuses', 'PASSIVE'), variant: 'danger' }
        ]
      }
    },

    methods: {
      onShow () {
        if (!this.license) return
        this.$store.dispatch('licenseUsages/clear')
        this.$store.dispatch('licenseUsages/getAllPaginated', {
          licenseId: this.license.licenseId
        })
        this.getProductSubtitle()
      },
      onHidden () {
        this.$store.dispatch('licenseUsages/clear')
        this.productSubtitle = null
        this.forceError = false
        this.showAssign = false
      },
      refresh () {
        const licenseId = this.license.licenseId
        this.$store.dispatch('usage/getUsageCount', licenseId)
        this.$store.dispatch('usage/getTotalUsageCount', licenseId)
        return this.$store.dispatch('licenseUsages/getAllPaginated', { licenseId })
      },
      statusVariant (status) {
        const option = this.overrideOptions.find(o => o.value === status)
        return option ? option.variant : 'secondary'
      },
      // Accepts both an enum (ACTIVE/PASSIVE/NO) and a boolean from the API.
      forcedValue (usage) {
        if (usage.forced === true) return usage.status
        if (usage.forced === 'ACTIVE' || usage.forced === 'PASSIVE') return usage.forced
        return 'NO'
      },
      async force (usage, forced) {
        if (this.forcedValue(usage) === forced) return
        const licenseId = this.license.licenseId
        this.updatingUserId = usage.userId
        this.forceError = false
        try {
          await LicenseService.setForcedOnLicenseUsage(usage.userId, licenseId, forced)
          await this.refresh()
        } catch (e) {
          this.forceError = true
        } finally {
          this.updatingUserId = null
        }
      },
      async getProductSubtitle () {
        this.productSubtitle = null
        if (!this.license.ean) return
        try {
          const products = await this.$store.dispatch('products/getProduct', { id: this.license.ean })
          const product = (products || []).find(p => p.ean === this.license.ean) || (products || [])[0]
          this.productSubtitle = product ? product.subtitle : null
        } catch (e) {
          this.productSubtitle = null
        }
      }
    },

    i18n: {
      messages: {
        en: {
          'licenseUsageDetails': 'License usage details',
          'firstAccess': 'First used',
          'latestAccess': 'Last used',
          'accessCount': 'Access count',
          'deleted': 'Deleted',
          'override': 'Override',
          'automatic': 'Automatic',
          'addUsers': 'Add users',
          'forceError': 'Could not update license usage',
        },
        nb: {
          'licenseUsageDetails': 'Lisensbruk detaljer',
          'firstAccess': 'Først brukt',
          'latestAccess': 'Sist brukt',
          'accessCount': 'Bruksteller',
          'deleted': 'Slettet',
          'override': 'Overstyring',
          'automatic': 'Automatisk',
          'addUsers': 'Legg til brukere',
          'forceError': 'Kunne ikke oppdatere lisensbruk',
        }
      }
    }
  }
</script>
